import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Ship, LogIn } from "lucide-react";
import { toast } from "sonner";

const TradeLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Redirect once auth state is committed
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/trade", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const result = await login(username, password);
    setLoading(false);
    if (result?.success) {
      toast.success("Welcome back!");
      // useEffect above will handle navigation once isAuthenticated flips
    } else {
      toast.error(result?.error || "Login failed");
    }
  };

  return (
    <div className="trade-section pt-20 min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 trade-grid-pattern opacity-50" />
      
      {/* Decorative blobs */}
      <motion.div
        className="absolute top-20 -right-20 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 -left-20 w-96 h-96 bg-[#10b981]/5 rounded-full blur-3xl"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md mx-6"
      >
        <div className="trade-card p-10 rounded-sm">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <motion.div
              className="w-16 h-16 trade-btn-primary flex items-center justify-center rounded-sm"
              whileHover={{ rotate: 5, scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Ship className="w-8 h-8" strokeWidth={1.5} />
            </motion.div>
          </div>

          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Private Access</p>
            <h1 className="text-3xl font-serif font-bold mb-2">
              Goldspot <span className="trade-gradient-text">Trade</span>
            </h1>
            <p className="text-sm text-white/50">Import Business Dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="trade-login-form">
            <div>
              <Label htmlFor="username" className="text-xs uppercase tracking-widest text-white/60">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="trade-input h-12 mt-2 rounded-sm"
                data-testid="trade-username-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-white/60">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="trade-input h-12 mt-2 rounded-sm"
                data-testid="trade-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full trade-btn-primary h-12 rounded-sm text-base"
              data-testid="trade-login-btn"
            >
              {loading ? "Signing in..." : (
                <>
                  <LogIn size={18} className="mr-2" />
                  Access Dashboard
                </>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-white/5 text-center">
            <p className="text-xs text-white/40 flex items-center justify-center gap-2">
              <Lock size={12} />
              Secure Private Access
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TradeLogin;
